import React, { createContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import * as auth from '../services/auth';
// The role hierarchy lives in one module (src/data/roleLevels.js) — this
// file used to carry its own copy, and the copy drifted from the backend.
import { roleLevel } from '../data/roleLevels';

export const AuthContext = createContext();


/**
 * The single role-assignment authority rule, extracted so it can be unit-tested
 * and reused without rendering the provider. A WILDCARD tier (Super Admin = 5,
 * Platform Admin = 6) may assign PEERS (>=) — this is what lets an SA grant
 * another SA and a PA grant another PA, while still stopping SA (5) from minting
 * a PA (6). Everyone else may assign STRICTLY BELOW their own level (>). Levels
 * are the backend ROLE_LEVELS; a role the compile-time map doesn't know (an
 * org-scoped CUSTOM role) is evaluated by passing its server-authoritative level
 * in as targetLevel, never guessed here.
 */
export const canAssignLevel = (myLevel, targetLevel) => {
  const my = Number.isFinite(myLevel) ? myLevel : 0;
  const target = Number.isFinite(targetLevel) ? targetLevel : 0;
  return my >= 5 ? my >= target : my > target;
};

// ---------------------------------------------------------------------------
// G539 — RBAC permission matrix lives ON THE SERVER only.
//
// The backend RbacService.ROLE_PERMS is the single source of truth.
// AuthService embeds the caller's effective permission set as
// `session.permissions` in every auth response (login, switchMembership,
// switchInstance), and the GET /api/v1/auth/permissions endpoint allows
// runtime refresh after an admin grants the user a new role.
//
// The frontend hasPermission() (below) reads from session.permissions.
// This eliminates the prior parallel ROLE_PERMS cache that drifted from
// the backend every time the RBAC matrix changed — a class of bug whose
// last instance (G538) hid every action button from assigned Tenant
// Admins because the frontend cache hadn't picked up the G535 perm grants.
//
// The static fallback below is retained ONLY for the dev-mode auth path
// (services/auth.js's _devMode) where the backend is unreachable. In
// production the server always returns session.permissions and that's
// what hasPermission() consults.
// ---------------------------------------------------------------------------
const DEV_MODE_FALLBACK_PERMS = {
  // Platform Admin — cross-customer authority, wildcard everything. Mirrors
  // the backend ROLE_PERMS["Platform Admin"]=Set.of("*") entry. The key was
  // previously missing here, which silently denied PA every hasPermission()
  // check in the dev-mode-fallback path (backend unreachable). Production
  // unaffected because server-side permissions arrive on every /auth/session
  // response, but local dev / disconnected-frontend smoke would lock PA out.
  'Platform Admin': ['*'],
  'Super Admin': ['*'],
  'Org Admin': [
    'org.read', 'org.manage', 'org.create', 'org.edit', 'org.delete',
    'tenant.manage', 'tenant.create', 'tenant.edit', 'tenant.delete',
    'user.manage', 'user.create', 'user.edit', 'user.delete', 'user.toggle',
    'group.manage', 'group.create', 'group.edit', 'group.delete', 'group.members',
    'agent.create', 'agent.edit', 'agent.delete', 'agent.read',
    // V163 — Agent Definition v1.0 write gate.
    'agent.edit_definition',
    'skill.create', 'skill.edit', 'skill.delete', 'skill.push', 'skill.promote', 'skill.revoke',
    // V161 — Skill Center / Skill Registry. skill.read drives the
    // Marketplace + Installed + Wizard-Managed read views; skill.install
    // gates Install/Disable on Marketplace cards.
    'skill.read', 'skill.install',
    'eval.run', 'eval.read',
    'execution.approve', 'execution.reject', 'execution.read',
    'phase.advance', 'phase.read',
    'assignment.manage', 'assignment.read',
    'connector.create', 'connector.edit', 'connector.delete', 'connector.test', 'connector.link',
    'instance.create', 'instance.edit', 'instance.delete', 'instance.read',
    'inference.read',  // inference CRUD reserved for Super Admin only
    'workflow.create', 'workflow.edit', 'workflow.delete', 'workflow.execute', 'workflow.cancel', 'workflow.read',
    'publish.create', 'publish.rollback', 'publish.read',
    // Agentic App Packs — view catalog/detail, provision a pack, and author drafts.
    'pack.read', 'pack.provision', 'pack.author',
  ],
  'Tenant Admin': [
    // G535 — mirror the backend RbacService Tenant Admin matrix. Frontend
    // ROLE_PERMS gates button visibility on the page; backend gates the
    // actual API call. Both lists must stay in sync, otherwise either
    // (a) the operator sees buttons that 403 on click, or (b) buttons
    // are hidden even though the operator can perform the action — the
    // current bug. Row-level scope enforcement in TenantService still
    // binds these reads/edits to the caller's active scope.
    'org.read',
    'tenant.read', 'tenant.edit',
    'user.manage', 'user.create', 'user.edit', 'user.toggle',  // lower roles only
    'group.manage', 'group.create', 'group.edit', 'group.delete', 'group.members',
    'agent.edit', 'agent.read',
    // V163 — Agent Definition v1.0 write gate.
    'agent.edit_definition',
    'skill.create', 'skill.edit', 'skill.push',
    // V161 — Skill Center / Skill Registry.
    'skill.read', 'skill.install',
    'eval.run', 'eval.read',
    'execution.approve', 'execution.reject', 'execution.read',
    'phase.read',
    'assignment.manage', 'assignment.read',
    'connector.create', 'connector.edit', 'connector.delete', 'connector.test', 'connector.link',
    // Tenant Admin gets full Instance CRUD inside the tenant they administer
    // (matches backend INSTANCE_PERMS union). instance.is_production_admin
    // gates the is_production / requires_promotion_approval toggles on the
    // Edit Instance slideout.
    'instance.read', 'instance.create', 'instance.edit', 'instance.delete',
    'instance.switch', 'instance.is_production_admin',
    'promotion.initiate', 'promotion.approve', 'promotion.reject',
    'promotion.rollback', 'promotion.bundle',
    'inference.read',  // inference CRUD reserved for Super Admin only
    'workflow.create', 'workflow.edit', 'workflow.delete', 'workflow.execute', 'workflow.cancel', 'workflow.read',
    'publish.read',
    'identity.read', 'identity.write',
    // Agentic App Packs — view catalog/detail, provision a pack, and author drafts.
    'pack.read', 'pack.provision', 'pack.author',
  ],
  // V191 / Phase-3 — Instance Admin manages one Instance's content.
  // Numeric peer of Developer in the legacy hierarchy; scope precision lives
  // in the user_org_roles grant row (tenant_id + instance_id both set).
  'Instance Admin': [
    // G535 — read access to parent org + tenant so the Multi-Tenancy
    // breadcrumb context renders. No write/edit on org or tenant.
    'org.read', 'tenant.read',
    'user.read', 'group.read',
    'agent.create', 'agent.read', 'agent.edit', 'agent.edit_definition',
    'skill.create', 'skill.edit', 'skill.push', 'skill.read', 'skill.install',
    'eval.run', 'eval.read',
    'execution.read', 'execution.approve', 'execution.reject',
    'phase.read',
    'assignment.manage', 'assignment.read',
    'connector.create', 'connector.edit', 'connector.test', 'connector.link',
    'inference.read',
    'workflow.create', 'workflow.edit', 'workflow.delete', 'workflow.execute', 'workflow.cancel', 'workflow.read',
    'publish.read',
    // Instance-scope CRUD + promotion authoring.
    'instance.read', 'instance.create', 'instance.edit', 'instance.delete', 'instance.switch',
    'instance.is_production_admin',
    'promotion.initiate', 'promotion.approve', 'promotion.reject', 'promotion.rollback', 'promotion.bundle',
  ],
  // V191 / Phase-3 — Promoter reads everywhere + can initiate promotions
  // (cannot approve own promotions — Phase-9 enforces separation).
  'Promoter': [
    // G535 — read parent org + tenant for Promotion Graph context.
    'org.read', 'tenant.read',
    'agent.read', 'skill.read', 'eval.read', 'execution.read',
    'phase.read', 'assignment.read', 'inference.read', 'workflow.read',
    'connector.test', 'publish.read', 'user.read', 'group.read',
    'instance.read', 'instance.switch',
    'promotion.initiate', 'promotion.bundle',
  ],
  'Developer': [
    // G535 — read parent org + tenant for breadcrumb context.
    'org.read', 'tenant.read',
    'agent.own', 'agent.create', 'agent.edit', 'agent.read',  // own = can edit/delete agents they created, not in Act/Published
    'pack.read',  // Agentic App Packs — Developers can view the catalog; provision/author are admin-scoped
    'skill.create', 'skill.edit', 'skill.push',
    // V161 — Developers can browse the Skill Center catalog (read-only)
    // but cannot install/disable skills for the tenant — that's a
    // tenant-admin operation per the production plan §9 RBAC matrix.
    'skill.read',
    'eval.run', 'eval.read',
    'execution.read',
    'phase.read',
    'assignment.read',
    'connector.create', 'connector.edit', 'connector.delete', 'connector.test', 'connector.link',
    'instance.read',
    'inference.read',
    'workflow.create', 'workflow.edit', 'workflow.delete', 'workflow.execute', 'workflow.read',
    'publish.read',
    'user.read', 'group.read',
  ],
  'Viewer': [
    // G535 — read parent org + tenant for breadcrumb context.
    'org.read', 'tenant.read',
    'agent.read',
    'eval.read',
    'execution.read',
    'phase.read',
    'assignment.read',
    'instance.read',
    'inference.read',
    'workflow.read',
    'publish.read',
    'user.read', 'group.read',
    // V161 — Viewers can browse the Skill Center catalog (read-only)
    // alongside their other read scopes. They cannot install/disable.
    'skill.read',
  ],
};

export function AuthProvider({ children }) {
  // RC-flash — Restore any cached session SYNCHRONOUSLY on the first render so a
  // returning user paints their authenticated shell immediately, instead of
  // flashing the global "Loading…" gate for the duration of checkDevMode()'s
  // /auth/health round-trip. initAuth() below still confirms dev-mode and
  // revalidates role/permissions from the live JWT (fire-and-forget) — identical
  // correctness model, minus the artificial blocking wait for logged-in users.
  const [boot] = useState(() => {
    try { return auth.restoreSession(); } catch (_e) { return null; }
  });
  const [user, setUser] = useState(boot?.user || null);
  const [session, setSession] = useState(boot?.session || null);
  const [memberships, setMemberships] = useState(boot?.memberships || []);
  const [isAuthenticated, setIsAuthenticated] = useState(!!(boot && boot.user));
  const [isDevMode, setIsDevMode] = useState(false);
  // Only block the app on first-load auth resolution when there is NO cached
  // session to render. A returning user starts un-gated; a genuine cold /
  // logged-out load still shows the loader until initAuth() resolves.
  const [loading, setLoading] = useState(!(boot && boot.user));

  // RC-93 — latest session/user snapshot for the cross-tab identity guard,
  // read inside event listeners that register once (avoids stale closures
  // without re-registering on every session change).
  const sessionRef = useRef(session);
  const userRef = useRef(user);
  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    const initAuth = async () => {
      // Check backend availability — determines if we fall back to dev mode
      const devMode = await auth.checkDevMode();
      setIsDevMode(devMode);

      if (!devMode) {
        // Restore session from sessionStorage after a full-page redirect (e.g., OAuth consent flow)
        const restored = auth.restoreSession();
        if (restored && restored.user) {
          setUser(restored.user);
          setSession(restored.session);
          setMemberships(restored.memberships || []);
          setIsAuthenticated(true);
          if (process.env.NODE_ENV === 'development') {
            console.log('[auth] Restored session from sessionStorage for', restored.user.email);
          }

          // DEF-EXEC-002 (Bug A) — Server-authoritative role refresh.
          // restoreSession() only reads from sessionStorage, which can drift
          // from what the JWT actually carries (role changes, deploy of a new
          // RBAC matrix, membership switch in another tab). Without this
          // refresh, the left-nav can show "Super Admin" while every
          // protected backend write is rejected as a different role.
          // Re-derive role/tenant/org/permissions from the live JWT via
          // /api/v1/auth/session and overwrite the restored session.
          // Fire-and-forget — if the call fails we keep the restored values
          // (the request will 401 on the next protected call, which already
          // triggers a normal sign-out path).
          auth.revalidateSession().then((fresh) => {
            if (!fresh) return;
            setSession((prev) => {
              const next = { ...(prev || {}), role: fresh.role };
              if (fresh.tenantId) next.tenantId = fresh.tenantId;
              if (fresh.orgId) next.orgId = fresh.orgId;
              // RC-137a — merge customerId from the server's JWT-sourced
              // truth. Without this, a Platform Admin who pinned a tenant
              // would see session.customerId snap back to its sessionStorage
              // value on next page mount, which could be stale relative to
              // the JWT cookie the browser is actually carrying.
              if (fresh.customerId) next.customerId = fresh.customerId;
              // RC-137f — merge display names. The chip renders these
              // (with fallback to activeMembership for non-PA roles).
              // Empty-string from BE = "no name resolved" — keep prior
              // value so we don't blank out the chip on a transient
              // lookup failure.
              if (fresh.tenantName) next.tenantName = fresh.tenantName;
              if (fresh.orgName) next.orgName = fresh.orgName;
              if (fresh.customerName) next.customerName = fresh.customerName;
              if (fresh.permissions) next.permissions = fresh.permissions;
              if (process.env.NODE_ENV === 'development' &&
                  prev && prev.role && fresh.role && prev.role !== fresh.role) {
                console.warn('[auth] Server-side role differs from cached session — corrected to', fresh.role);
              }
              return next;
            });
          }).catch(() => { /* swallow — restored session still in effect */ });
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[auth] Backend unreachable — dev mode active. Is the backend running?');
        }
      }

      setLoading(false);
    };
    initAuth();
  }, []);

  // ---------------------------------------------------------------
  // Login (first factor)
  // ---------------------------------------------------------------
  const login = useCallback(async (email, password) => {
    try {
      const result = await auth.smartLogin(email, password);
      if (!result.ok) return { ok: false, error: result.error };

      if (result.mfaRequired) {
        return { ok: true, mfaRequired: true, mfaMethod: result.mfaMethod };
      }

      // Full auth complete (no MFA)
      setUser(result.user);
      setSession(result.session);
      setMemberships(result.memberships || []);
      setIsAuthenticated(true);
      return { ok: true, mfaRequired: false };
    } catch (error) {
      console.error('[auth] Login error:', error);
      return { ok: false, error: 'Login failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // MFA Verify (second factor)
  // ---------------------------------------------------------------
  const verifyMfa = useCallback(async (code, method) => {
    try {
      const result = await auth.smartVerifyMfa(code, method);
      if (!result.ok) return { ok: false, error: result.error };

      setUser(result.user);
      setSession(result.session);
      setMemberships(result.memberships || []);
      setIsAuthenticated(true);

      const token = auth.getAccessToken();
      if (token) auth.setAccessToken?.(token);

      return { ok: true };
    } catch (error) {
      console.error('[auth] MFA error:', error);
      return { ok: false, error: 'Verification failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // SSO Login
  // ---------------------------------------------------------------
  const ssoLogin = useCallback(async (provider) => {
    try {
      const result = await auth.smartSsoInitiate(provider);
      if (!result.ok) return { ok: false, error: result.error };

      if (result.devFallback) {
        return { ok: true, mfaRequired: true, mfaMethod: 'totp', user: result.user };
      }

      if (result.authorizationUrl) {
        return { ok: true, authorizationUrl: result.authorizationUrl };
      }

      return { ok: true, mfaRequired: result.mfaRequired, user: result.user };
    } catch (error) {
      console.error('[auth] SSO error:', error);
      return { ok: false, error: 'SSO failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // SSO Home Realm Discovery — resolve provider(s) from a work email
  // ---------------------------------------------------------------
  const ssoDiscover = useCallback(async (email) => {
    try {
      return await auth.discoverSsoProviders(email);
    } catch (error) {
      console.error('[auth] SSO discover error:', error);
      return { ok: false, error: 'Unable to check single sign-on', providers: [] };
    }
  }, []);

  // ---------------------------------------------------------------
  // Register
  // ---------------------------------------------------------------
  const register = useCallback(async (firstName, lastName, email, password, mobileNumber) => {
    try {
      const result = await auth.registerUser(firstName, lastName, email, password, mobileNumber);
      if (!result.ok) return { ok: false, error: result.error };
      return { ok: true, message: result.message };
    } catch (error) {
      console.error('[auth] Register error:', error);
      return { ok: false, error: 'Registration failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // Forgot Password
  // ---------------------------------------------------------------
  const forgotPassword = useCallback(async (email) => {
    try {
      const result = await auth.requestForgotPassword(email);
      return { ok: true, message: result.message };
    } catch (error) {
      console.error('[auth] Forgot password error:', error);
      return { ok: false, error: 'Request failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // Logout
  // ---------------------------------------------------------------
  const logout = useCallback(async () => {
    try {
      await auth.logout();
      setUser(null);
      setSession(null);
      setMemberships([]);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('[auth] Logout error:', error);
    }
  }, []);

  // ---------------------------------------------------------------
  // G410 — Membership switch
  //   Re-scopes the current session to a different (org, tenant, role)
  //   tuple. The server invalidates the old token (via the DB-backed
  //   blacklist watermark from G408) and returns a fresh JWT bound to
  //   the chosen membership. We update React state in place, then the
  //   caller is expected to full-page reload so every page re-fetches
  //   data under the new scope.
  // ---------------------------------------------------------------
  const switchMembership = useCallback(async (membershipId) => {
    try {
      const result = await auth.switchMembership(membershipId);
      if (!result.ok) return result;
      // Re-hydrate the auth state. The membership list itself doesn't
      // change (same user, same set of memberships) but session.role /
      // session.tenantId / session.orgId DO change, and that drives
      // every downstream RBAC + data-scoping decision.
      const restored = auth.restoreSession();
      if (restored && restored.user) {
        setUser(restored.user);
        setSession(restored.session);
        setMemberships(restored.memberships || []);
      }
      return result;
    } catch (error) {
      console.error('[auth] switchMembership error:', error);
      return { ok: false, error: 'Switch failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // V191 / Phase-6 — Instance switcher.
  //
  // Calls the Phase-4 POST /api/v1/auth/switch-instance backend
  // endpoint via services/auth.switchInstance. On success the server
  // re-issues the JWT with a new `iid` claim (membership and tenant
  // stay the same) and blacklists the previous token. We re-hydrate
  // the auth state from sessionStorage so session.instanceId reflects
  // the new active Instance.
  //
  // Callers are expected to full-page reload after a successful switch
  // (caches keyed on instanceId need to refetch). DashboardLayoutV2's
  // user-menu handler does the reload.
  // ---------------------------------------------------------------
  const switchInstance = useCallback(async (instanceId) => {
    try {
      const result = await auth.switchInstance(instanceId);
      if (!result.ok) return result;
      const restored = auth.restoreSession();
      if (restored && restored.user) {
        setUser(restored.user);
        setSession(restored.session);
        // memberships list doesn't change on instance switch.
      }
      return result;
    } catch (error) {
      console.error('[auth] switchInstance error:', error);
      return { ok: false, error: 'Switch failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // G583 — V208 / Phase D-2 Customer switch.
  //
  // Wraps services/auth.switchCustomer, which persists the freshly
  // minted JWT before we navigate. Without this AuthContext-mediated
  // path, CustomersPageV2's "Switch into" button silently dropped the
  // new token on the floor, and the next request landed with a stale
  // JWT — the JwtAuthFilter then 401'd it with a role-change error,
  // bouncing the user to /login.
  // ---------------------------------------------------------------
  const switchCustomer = useCallback(async (customerId) => {
    try {
      const result = await auth.switchCustomer(customerId);
      if (!result.ok) return result;
      const restored = auth.restoreSession();
      if (restored && restored.user) {
        setUser(restored.user);
        setSession(restored.session);
        setMemberships(restored.memberships || []);
      }
      return result;
    } catch (error) {
      console.error('[auth] switchCustomer error:', error);
      return { ok: false, error: 'Switch failed' };
    }
  }, []);

  /* RC-137a — Pin Platform Admin into a specific tenant.
   *
   * Companion to switchCustomer. switchCustomer moves the PA into a
   * customer scope (cid claim) but leaves tenantId null and orgId at
   * the sentinel — so tenant-filtered list endpoints still return 0
   * for PA. switchTenant goes one level deeper: the BE re-issues the
   * JWT with new tid + auto-derived oid + auto-derived cid (server-
   * derived from the tenant→org→customer chain so a malicious FE can't
   * spoof the parent ids). After the switch, every page filtering by
   * session.tenantId resolves the same way it does for a TA/OA pinned
   * to that tenant.
   *
   * Same session-restore pattern as switchCustomer — auth.switchTenant
   * persists the new JWT, then we re-read sessionStorage to update the
   * in-memory React state so downstream consumers see the fresh tid. */
  const switchTenant = useCallback(async (tenantId) => {
    try {
      const result = await auth.switchTenant(tenantId);
      if (!result.ok) return result;
      const restored = auth.restoreSession();
      if (restored && restored.user) {
        setUser(restored.user);
        setSession(restored.session);
        setMemberships(restored.memberships || []);
      }
      return result;
    } catch (error) {
      console.error('[auth] switchTenant error:', error);
      return { ok: false, error: 'Switch failed' };
    }
  }, []);

  // ---------------------------------------------------------------
  // RBAC helpers
  // ---------------------------------------------------------------
  /**
   * G539 — hasPermission() now reads from the server-authoritative
   * permission set embedded in `session.permissions`. Backend
   * AuthService.permissionsForRole(activeRole) populates this on login,
   * membership switch and instance switch; sessionStorage round-trips it
   * across full-page reloads via _persistSession.
   *
   * Fallback to the static DEV_MODE_FALLBACK_PERMS map happens ONLY when
   * the backend is unreachable (dev mode) — see services/auth.js
   * _devMode. Production never hits this fallback.
   */
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    // Server-authoritative path.
    const serverPerms = Array.isArray(session?.permissions) ? session.permissions : null;
    if (serverPerms) {
      return serverPerms.includes('*') || serverPerms.includes(permission);
    }
    // Dev-mode / unreachable-backend fallback. Logs once so the gap is
    // visible in the console rather than silently false-positiving.
    //
    // DEF-EXEC-002 / V208 — role MUST come from session (JWT-resolved
    // membership), not from user.role (app_users user-level fallback).
    // A user with multiple memberships can have user.role='Super Admin'
    // but session.role='Tenant Admin' if they switched into a tenant-scoped
    // membership. The backend RBAC matrix keys off session.role, so the
    // frontend must mirror that or local permission checks will drift.
    const role = session?.role || user.role || 'Viewer';
    const perms = DEV_MODE_FALLBACK_PERMS[role] || [];
    return perms.includes(permission) || perms.includes('*');
  }, [user, session]);

  /**
   * G539 — Runtime refresh of the caller's permission set. Hits
   * GET /api/v1/auth/permissions and merges the result into session.
   * Use after any operation that may have changed the user's role/scope
   * out-of-band (an admin in another tab promoted them; SSO group sync
   * updated their role; etc.). The normal login / switchMembership /
   * switchInstance paths already refresh perms inline, so most callers
   * never need this — it's an escape hatch.
   */
  const refreshPermissions = useCallback(async () => {
    try {
      const mod = await import('../services/api');
      const res = await mod.getPermissions();
      if (res && res.ok !== false) {
        const payload = Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        if (payload && Array.isArray(payload.permissions)) {
          setSession((prev) => prev ? { ...prev, role: payload.role || prev.role, permissions: payload.permissions } : prev);
          return { ok: true };
        }
      }
      return { ok: false, error: 'refreshPermissions failed' };
    } catch (e) {
      return { ok: false, error: e?.message || String(e) };
    }
  }, []);

  /* DEF-PERMS-CACHE-SYNC-001 — wire api.js perms-rev listener to
   * refreshPermissions(). Every API response carries X-Perms-Rev from the
   * backend PermsRevFilter. api.js compares against its module-level cache
   * and fires this listener (debounced 1s) when the server perm map has
   * changed since last seen. We re-fetch perms, which updates
   * session.permissions and bubbles to every hasPermission() call site.
   * Zero login/logout cycle required; zero extra requests when stable.
   *
   * Page-load priming: the listener alone can't detect deploys that happen
   * BETWEEN page loads (the JWT carries permissions from when it was
   * issued; the cached `_lastSeenPermsRev` in api.js resets on every full
   * page reload because the module re-initializes). To close that gap we
   * also force a single refreshPermissions() on mount when a user session
   * is present. Result: page-load picks up any deploy that happened since
   * the JWT was issued; in-session deploys are caught by the X-Perms-Rev
   * listener; steady-state has zero overhead (the listener's first
   * observation primes the cache; subsequent matches are no-ops). */
  useEffect(() => {
    let active = true;
    let attached = false;
    (async () => {
      try {
        const mod = await import('../services/api');
        if (!active || typeof mod.setPermsRevListener !== 'function') return;
        mod.setPermsRevListener(async () => {
          if (!user) return;
          try {
            await refreshPermissions();
          } catch (e) {
            console.warn('perms-rev triggered refresh failed:', e);
          }
        });
        attached = true;

        // Page-load priming refresh — only when a real user session exists.
        // This is the one extra request per page-load that closes the
        // deploy-between-page-loads gap. Subsequent in-page navigations
        // do NOT re-fire this (React only mounts the AuthContext once per
        // tab session); only a full reload re-runs it.
        if (user) {
          try {
            await refreshPermissions();
          } catch (e) {
            console.warn('perms-rev page-load priming refresh failed:', e);
          }
        }
      } catch (e) {
        console.warn('perms-rev listener wire-up failed:', e);
      }
    })();
    return () => {
      active = false;
      if (attached) {
        import('../services/api').then((mod) => {
          if (typeof mod.setPermsRevListener === 'function') mod.setPermsRevListener(null);
        }).catch(() => {});
      }
    };
  }, [user, refreshPermissions]);

  // ---------------------------------------------------------------
  // RC-93 — Cross-tab identity-reconciliation guard.
  //
  // SEC-25 moved auth to a SHARED HttpOnly cookie (per-browser, not per-tab).
  // If a DIFFERENT account signs in on another tab/window of the SAME browser,
  // its login overwrites that one cookie, and this tab's requests then silently
  // authenticate as the new principal — while this tab's per-tab sessionStorage
  // still DISPLAYS the old identity (e.g. a "Platform Admin" badge sitting over
  // a Super-Admin cookie). That divergence is the "PA mid-session loses scope"
  // report.
  //
  // On focus / visibility / a slow poll we ask the server who the cookie
  // actually is (revalidateSession → /auth/session). If the authoritative
  // userId no longer matches THIS tab's cached session, the cookie was taken
  // over: we tear down only THIS tab's cached identity and bounce to /login —
  // WITHOUT calling logout (which would clear the shared cookie and disturb the
  // other tab). A role-only drift for the SAME user is re-synced in place.
  // A null result (transient/expired) is ignored — the next protected call's
  // 401 already drives the normal sign-out path, so we never false-logout on a
  // network blip.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated || isDevMode) return undefined;
    let cancelled = false;
    let lastCheck = 0;

    const reconcile = async () => {
      const now = Date.now();
      if (now - lastCheck < 4000) return;           // throttle bursty focus events
      lastCheck = now;
      let fresh;
      try { fresh = await auth.revalidateSession(); } catch (_) { return; }
      if (cancelled || !fresh) return;               // transient/expired → leave to 401 path
      const cachedUserId = sessionRef.current?.userId || userRef.current?.id || null;
      if (cachedUserId && fresh.userId && String(fresh.userId) !== String(cachedUserId)) {
        // Principal takeover — a different account owns the cookie now.
        console.warn('[auth] RC-93: session principal changed on this browser — bouncing to login.');
        try {
          sessionStorage.removeItem('_authUser');
          sessionStorage.removeItem('_authSession');
          sessionStorage.removeItem('_authMemberships');
        } catch (_) { /* ignore */ }
        setUser(null); setSession(null); setMemberships([]); setIsAuthenticated(false);
        // Full navigation resets all in-memory caches. Do NOT call auth.logout()
        // here — that clears the shared cookie and would log the other tab out too.
        window.location.assign('/login?reason=session_changed');
        return;
      }
      // Same principal — keep the displayed role/permissions honest if drifted.
      if (fresh.role && sessionRef.current?.role && fresh.role !== sessionRef.current.role) {
        setSession((prev) => (prev ? {
          ...prev,
          role: fresh.role,
          permissions: fresh.permissions || prev.permissions,
          tenantId: fresh.tenantId || prev.tenantId,
          orgId: fresh.orgId || prev.orgId,
        } : prev));
      }
    };

    const onVisibility = () => { if (document.visibilityState === 'visible') reconcile(); };
    window.addEventListener('focus', reconcile);
    document.addEventListener('visibilitychange', onVisibility);
    const poll = setInterval(reconcile, 30000);     // catches a focused tab while another logs in
    // Run once on mount of the guard (covers a reload that restored a stale tab).
    reconcile();

    return () => {
      cancelled = true;
      window.removeEventListener('focus', reconcile);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(poll);
    };
  }, [isAuthenticated, isDevMode]);

  // Shorthand: can the current user write/edit (not read-only)
  // DEF-EXEC-002 / V208 — read role from session (JWT-resolved membership)
  // rather than user.role (user-level fallback). See hasPermission comment.
  const canWrite = useCallback(() => {
    if (!user) return false;
    const role = session?.role || user.role || 'Viewer';
    return role !== 'Viewer';
  }, [user, session]);

  // Shorthand: can approve promotions, advance phases, publish
  const canPromote = useCallback(() => {
    return hasPermission('phase.advance') || hasPermission('publish.create');
  }, [hasPermission]);

  // DEF-EXEC-002 / V208 — both Super Admin and Platform Admin pass.
  // Aligns with SecurityContext.isSuperAdmin() on the backend (the canonical
  // "wildcard tier" check). Reads from session.role first so a Platform
  // Admin who hasn't switched into a customer still tests as superadmin.
  const isSuperAdmin = useCallback(() => {
    const role = session?.role || user?.role;
    return role === 'Super Admin' || role === 'Platform Admin';
  }, [user, session]);

  // STRICT Platform-Admin check — true ONLY for the level-6 global tier, never
  // for a customer-scoped Super Admin. Use this (not isSuperAdmin, which
  // deliberately conflates PA+SA) for anything that is genuinely platform-only:
  // provisioning/managing SysLets (system agents), platform housekeeping, the
  // global role matrix, etc. Mirrors SecurityContext.isPlatformAdmin() on the
  // backend. Reads session.role first so a PA who hasn't switched into a
  // customer still resolves correctly.
  const isPlatformAdmin = useCallback(() => {
    const role = session?.role || user?.role;
    return role === 'Platform Admin';
  }, [user, session]);

  // Role level comparison: can the current user manage a target role?
  // DEF-EXEC-002 / V208 — derive my level from session.role; the canManageRole
  // call sites (Team page Edit / Create user modals) need the EFFECTIVE
  // role for the current scope, not the user-level fallback.
  // RBAC Phase 2 — targetLevelOverride lets a caller supply the AUTHORITATIVE
  // level for a role the compile-time ROLE_LEVELS map doesn't know (org-scoped
  // CUSTOM roles, which the server catalog reports at CUSTOM_ROLE_LEVEL). Without
  // it a custom role resolved to level 0 here, so every actor appeared able to
  // grant it. Built-in call sites pass one arg and are unchanged; custom-role
  // pickers pass the level from getCustomRoles(). Single authority rule, no
  // duplicate level logic at the call sites.
  const canManageRole = useCallback((targetRole, targetLevelOverride) => {
    if (!user) return false;
    const myRole = session?.role || user.role;
    const myLevel = roleLevel(myRole);
    const targetLevel = (typeof targetLevelOverride === 'number')
      ? targetLevelOverride
      : roleLevel(targetRole);
    // Authority rule lives in the shared canAssignLevel helper (unit-tested).
    // NOTE: TeamPageV2's canManageTargetUser uses PA-bypass + strict `>`
    // (doc §1.5 "strictly below"); the two FE paths differ on whether a
    // wildcard role may assign a peer — flagged for reconciliation.
    return canAssignLevel(myLevel, targetLevel);
  }, [user, session]);

  // Developer ownership check: can edit/delete agent if they created it AND it's not in Act/Published
  const canManageOwnAgent = useCallback((agent) => {
    if (!user) return false;
    if (hasPermission('agent.delete')) return true;  // Admin roles
    if (!hasPermission('agent.own')) return false;
    const isOwner = agent?.createdBy === user.id || agent?.createdBy === session?.userId;
    const inActOrPublished = agent?.phase?.toLowerCase() === 'act' || agent?.published;
    return isOwner && !inActOrPublished;
  }, [user, session, hasPermission]);

  /* RC-93a — STRUCTURAL FIX (mirrors RC-92a on PlatformContext).
   *
   * Pre-RC-93a, `value` was constructed inline every render. Every
   * useAuth() consumer therefore received a NEW context object per
   * render, which meant `session` was a new object reference per
   * render (even though its string fields were stable). For most
   * consumers this was harmless because they read primitive fields
   * — but it set off a class of subtle bugs where developers wrote
   * `useEffect(..., [session])` thinking they were tracking real
   * session changes; they were actually re-firing on every parent
   * render.
   *
   * RC-93 (V2 marketplace loop persisting after RC-91 + RC-92a) made
   * AuthContext memoization mandatory: only an unmemoized auth value
   * could explain hundreds of /api/v1/agents requests/sec from a
   * page whose effect dep array was correctly [orgId, tenantId]
   * (strings) — IF those strings were ever transiently undefined
   * mid-fetch, the effect would re-fire on every render that flipped
   * the value back. Memoizing the value object also stabilizes
   * `session` so the entire dependency chain hardens.
   *
   * Adding a new field? Append to BOTH the returned object AND the
   * deps array below. They MUST stay in sync. */
  const value = useMemo(() => ({
    user,
    session,
    memberships,
    isAuthenticated,
    isDevMode,
    loading,
    login,
    verifyMfa,
    ssoLogin,
    ssoDiscover,
    switchMembership,
    switchInstance,
    switchCustomer,
    switchTenant,    // RC-137a — PA tenant-pin (peer of switchCustomer)
    register,
    forgotPassword,
    logout,
    canWrite,
    canPromote,
    hasPermission,
    refreshPermissions,  // G539 — runtime perm-set refresh
    isSuperAdmin,
    isPlatformAdmin,     // STRICT PA-only (never customer-scoped SA)
    canManageRole,
    canManageOwnAgent,
    // DEF-EXEC-002 / V208 — currentRole and roleLevel are derived from
    // session.role (the JWT-resolved membership role), with user.role as
    // a fallback ONLY for legacy accounts that pre-date the membership
    // bridge. Before this fix, a user with multiple memberships could
    // have currentRole='Super Admin' (from app_users.role) while the
    // backend JWT carried 'Tenant Admin' — every page rendered for the
    // wrong scope and writes returned 403/503 from backend RBAC.
    currentRole: session?.role || user?.role || 'Viewer',
    roleLevel: roleLevel(session?.role || user?.role),
  }), [
    // ── State slots ──────────────────────────────────────────────
    user, session, memberships, isAuthenticated, isDevMode, loading,
    // ── Memoized callbacks ───────────────────────────────────────
    login, verifyMfa, ssoLogin, ssoDiscover, switchMembership, switchInstance,
    switchCustomer, switchTenant, register, forgotPassword, logout,
    canWrite, canPromote, hasPermission, refreshPermissions,
    isSuperAdmin, isPlatformAdmin, canManageRole, canManageOwnAgent,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
